import React, { useEffect, useMemo, useState } from 'react';
import {
  readNotifications, readActivity, readPrefs, writePrefs,
  dismissNotification, dismissActivity, markAllNotificationsRead,
  seedIfEmpty, PREF_META,
  fetchServerNotifications, markServerNotificationRead,
} from '../../lib/notifications';

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 2)  return 'just now';
  if (m < 60) return `${m}m`;
  if (h < 24) return `${h}h`;
  if (d < 7)  return `${d}d`;
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Slide-out notifications panel. Two tabs — Notifications | Activity — each
 * with dismissible rows. Gear icon opens preferences for what shows up.
 */
export default function NotificationsPanel({
  open, onClose,
  user, profile,
  onCountChange,
}) {
  const userId = user?.id || user?.email;
  const [tab, setTab]             = useState('notifications');
  const [notifs, setNotifs]       = useState([]);
  const [activity, setActivity]   = useState([]);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs]         = useState(() => readPrefs(userId));

  // Load + seed on open. Mini-spec #7 also fetches server-side
  // notifications (cron-driven content like the morning briefing) and
  // merges them into the local list.
  useEffect(() => {
    if (!open || !userId) return;
    seedIfEmpty(userId, profile);
    const local = readNotifications(userId);
    setNotifs(local);
    setActivity(readActivity(userId));
    setPrefs(readPrefs(userId));
    // Async merge of server notifications.
    fetchServerNotifications(userId).then(server => {
      if (!server || server.length === 0) return;
      // Dedupe by id (server rows already prefixed `srv:`).
      const ids = new Set(local.map(n => n.id));
      const merged = [...server.filter(n => !ids.has(n.id)), ...local]
        .sort((a, b) => (b.ts || 0) - (a.ts || 0));
      setNotifs(merged);
    });
  }, [open, userId, profile]);

  // Mark as read when user opens the Notifications tab. Server-side rows
  // get marked read in the DB so the bell badge clears across devices.
  useEffect(() => {
    if (!open || !userId) return;
    if (tab === 'notifications') {
      const next = markAllNotificationsRead(userId);
      setNotifs(prev => {
        // Merge the freshly-marked locals with any server rows already in state.
        const serverRows = prev.filter(n => n.source === 'server');
        return [...serverRows.map(n => ({ ...n, unread: false })), ...next]
          .sort((a, b) => (b.ts || 0) - (a.ts || 0));
      });
      // Fire DB updates for unread server rows. Fire-and-forget.
      Promise.all(
        (notifs || [])
          .filter(n => n.source === 'server' && n.serverId && n.unread)
          .map(n => markServerNotificationRead(userId, n.serverId))
      ).catch(() => {});
      onCountChange?.();
    }
  }, [open, tab, userId, onCountChange]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === 'Escape') onClose?.(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Respect prefs — hide items whose kind is disabled
  const visibleNotifs = useMemo(
    () => notifs.filter(n => !n.prefKey || prefs[n.prefKey] !== false),
    [notifs, prefs]
  );
  const visibleActivity = useMemo(
    () => activity.filter(a => !a.prefKey || prefs[a.prefKey] !== false),
    [activity, prefs]
  );

  function handleDismissNotif(id) {
    const next = dismissNotification(userId, id);
    setNotifs(next);
    onCountChange?.();
  }
  function handleDismissActivity(id) {
    const next = dismissActivity(userId, id);
    setActivity(next);
    onCountChange?.();
  }
  function handlePrefChange(key, val) {
    const next = { ...prefs, [key]: val };
    setPrefs(next);
    writePrefs(userId, next);
  }

  if (!open) return null;

  return (
    <>
      <div className="ws-notif-backdrop" onClick={onClose} />
      <aside className="ws-notif-panel" role="dialog" aria-label="Notifications">
        <header className="ws-notif-head">
          <div className="ws-notif-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={tab === 'notifications'}
              className={`ws-notif-tab${tab === 'notifications' ? ' active' : ''}`}
              onClick={() => setTab('notifications')}
            >
              Notifications
              {notifs.filter(n => n.unread).length > 0 && (
                <span className="ws-notif-tab-dot" />
              )}
            </button>
            <button
              role="tab"
              aria-selected={tab === 'activity'}
              className={`ws-notif-tab${tab === 'activity' ? ' active' : ''}`}
              onClick={() => setTab('activity')}
            >
              Activity
              {activity.filter(a => a.unread).length > 0 && (
                <span className="ws-notif-tab-dot" />
              )}
            </button>
          </div>
          <div className="ws-notif-head-actions">
            <button
              type="button"
              className="ws-notif-icon-btn"
              onClick={() => setPrefsOpen(v => !v)}
              aria-label="Notification settings"
              aria-pressed={prefsOpen}
              title="Notification settings"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>
            <button
              type="button"
              className="ws-notif-icon-btn"
              onClick={onClose}
              aria-label="Close notifications"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </header>

        {prefsOpen ? (
          <PrefsView prefs={prefs} onChange={handlePrefChange} onBack={() => setPrefsOpen(false)} />
        ) : (
          <div className="ws-notif-body">
            {tab === 'notifications' && (
              <ItemList
                items={visibleNotifs}
                empty="You're all caught up."
                onDismiss={handleDismissNotif}
              />
            )}
            {tab === 'activity' && (
              <ItemList
                items={visibleActivity}
                empty="No activity yet. Follows, guest requests, and sponsorship offers land here."
                onDismiss={handleDismissActivity}
              />
            )}
          </div>
        )}
      </aside>
    </>
  );
}

function ItemList({ items, empty, onDismiss }) {
  if (items.length === 0) {
    return <div className="ws-notif-empty">{empty}</div>;
  }
  return (
    <div className="ws-notif-list">
      {items.map(item => (
        <article key={item.id} className={`ws-notif-item${item.unread ? ' ws-notif-item-unread' : ''}`}>
          <div className="ws-notif-item-body">
            <div className="ws-notif-item-head">
              <div className="ws-notif-item-title">{item.title}</div>
              <div className="ws-notif-item-time">{timeAgo(item.ts)}</div>
            </div>
            {item.body && <div className="ws-notif-item-text">{item.body}</div>}
          </div>
          <button
            type="button"
            className="ws-notif-item-dismiss"
            onClick={() => onDismiss(item.id)}
            aria-label="Dismiss"
            title="Dismiss"
          >✕</button>
        </article>
      ))}
    </div>
  );
}

function PrefsView({ prefs, onChange, onBack }) {
  const notifs  = PREF_META.filter(p => p.kind === 'notification');
  const acts    = PREF_META.filter(p => p.kind === 'activity');
  return (
    <div className="ws-notif-prefs">
      <button className="ws-notif-prefs-back" onClick={onBack}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        <span>Back</span>
      </button>

      <PrefGroup title="Notifications" items={notifs} prefs={prefs} onChange={onChange} />
      <PrefGroup title="Activity"      items={acts}   prefs={prefs} onChange={onChange} />

      <div className="ws-notif-prefs-note">
        Disabling a category hides any pending items of that kind from the panel.
      </div>
    </div>
  );
}

function PrefGroup({ title, items, prefs, onChange }) {
  return (
    <section className="ws-notif-prefs-group">
      <h3 className="ws-notif-prefs-title">{title}</h3>
      {items.map(p => (
        <label key={p.key} className="ws-notif-pref">
          <div className="ws-notif-pref-text">
            <div className="ws-notif-pref-label">{p.label}</div>
            <div className="ws-notif-pref-desc">{p.desc}</div>
          </div>
          <input
            type="checkbox"
            className="ws-notif-pref-toggle"
            checked={prefs[p.key] !== false}
            onChange={(e) => onChange(p.key, e.target.checked)}
          />
        </label>
      ))}
    </section>
  );
}
